import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  Box,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  Alert,
  Stack,
  Avatar,
  Tooltip,
  Fab
} from '@mui/material';
import {
  Add as AddIcon,
  CalendarMonth as CalendarIcon,
  Restaurant as RestaurantIcon,
  SwapHoriz as SwapIcon,
  SkipNext as SkipIcon,
  Timer as TimerIcon,
  LocalFireDepartment as CaloriesIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { format, addDays, startOfWeek } from 'date-fns';
import { GENERATE_MEAL_PLAN, SWAP_RECIPE, SKIP_MEAL } from '../graphql/mutations';
import { GET_ACTIVE_MEAL_PLAN } from '../graphql/queries';
import { ON_MEAL_PLAN_UPDATE } from '../graphql/subscriptions';
import RecipeModal from '../components/RecipeModal';

const MEAL_TYPES = ['breakfast', 'lunch', 'dinner'];
const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const MealPlans: React.FC = () => {
  const [householdId] = useState('default-household');
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<any>(null);
  const [selectedRecipe, setSelectedRecipe] = useState<any>(null);
  const [preferences, setPreferences] = useState<{
    maxPrepTime: number;
    cuisinePreferences: string[];
    dietaryRestrictions: string[];
    targetCalories: number;
  }>({
    maxPrepTime: 45,
    cuisinePreferences: [],
    dietaryRestrictions: [],
    targetCalories: 2000
  });

  const { data: activePlanData, loading: planLoading, refetch, subscribeToMore } = useQuery(GET_ACTIVE_MEAL_PLAN, {
    variables: { householdId },
    skip: !householdId
  });

  // Subscribe to real-time meal plan updates
  React.useEffect(() => {
    if (!householdId || !subscribeToMore) return;

    const unsubscribe = subscribeToMore({
      document: ON_MEAL_PLAN_UPDATE,
      variables: { householdId },
      updateQuery: (prev, { subscriptionData }) => {
        if (!subscriptionData.data) return prev;
        
        const updatedPlan = subscriptionData.data.onMealPlanUpdate;
        
        // Only update if it's the active plan
        if (prev.getActiveMealPlan?.planId === updatedPlan.planId) {
          return {
            ...prev,
            getActiveMealPlan: updatedPlan
          };
        }
        
        return prev;
      },
      onError: (error) => {
        console.error('Meal plan subscription error:', error);
      }
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [householdId, subscribeToMore]);

  const [generateMealPlan, { loading: generating }] = useMutation(GENERATE_MEAL_PLAN, {
    onCompleted: () => {
      setGenerateDialogOpen(false);
      refetch();
    }
  });

  const [swapRecipe] = useMutation(SWAP_RECIPE, {
    onCompleted: () => refetch()
  });

  const [skipMeal] = useMutation(SKIP_MEAL, {
    onCompleted: () => refetch()
  });

  const handleGeneratePlan = () => {
    const weekStart = format(startOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd');
    generateMealPlan({
      variables: {
        householdId,
        input: {
          weekStart,
          preferences
        }
      }
    });
  };

  const handleSwapRecipe = (slotIndex: number, newRecipeId: string) => {
    if (activePlanData?.getActiveMealPlan) {
      swapRecipe({
        variables: {
          householdId,
          planId: activePlanData.getActiveMealPlan.planId,
          slotIndex,
          newRecipeId
        }
      });
    }
  };

  const handleSkipMeal = (slotIndex: number) => {
    if (activePlanData?.getActiveMealPlan) {
      skipMeal({
        variables: {
          householdId,
          planId: activePlanData.getActiveMealPlan.planId,
          slotIndex
        }
      });
    }
  };

  const getMealsByDay = () => {
    if (!activePlanData?.getActiveMealPlan?.planSlots) return {};
    
    const mealsByDay: any = {};
    const weekStart = startOfWeek(new Date(), { weekStartsOn: 1 });
    
    DAYS.forEach((day, index) => {
      const date = format(addDays(weekStart, index), 'yyyy-MM-dd');
      mealsByDay[day] = {
        date,
        meals: {
          breakfast: null,
          lunch: null,
          dinner: null
        }
      };
    });

    activePlanData.getActiveMealPlan.planSlots.forEach((slot: any, index: number) => {
      const slotDate = new Date(slot.date);
      const dayName = DAYS[slotDate.getDay() === 0 ? 6 : slotDate.getDay() - 1];
      if (mealsByDay[dayName]) {
        mealsByDay[dayName].meals[slot.mealType] = { ...slot, slotIndex: index };
      }
    });

    return mealsByDay;
  };

  const MealCard = ({ meal, slotIndex }: any) => {
    if (!meal) {
      return (
        <Card sx={{ height: 140, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.100' }}>
          <Typography color="text.secondary">No meal planned</Typography>
        </Card>
      );
    }

    const isSkipped = meal.status === 'skipped';
    
    return (
      <Card sx={{ height: 140, position: 'relative', opacity: isSkipped ? 0.5 : 1, cursor: 'pointer' }} onClick={() => setSelectedRecipe(meal)}>
        {meal.recipeImage && (
          <CardMedia
            component="img"
            height="70"
            image={meal.recipeImage}
            alt={meal.recipeName}
          />
        )}
        <CardContent sx={{ p: 1.5 }}>
          <Typography variant="subtitle2" noWrap fontWeight="bold">
            {meal.recipeName || meal.mealType}
          </Typography>
          <Box display="flex" alignItems="center" gap={1} mt={0.5}>
            <Chip
              size="small"
              icon={<TimerIcon />}
              label={`${meal.recipeStub?.readyInMinutes || 30}m`}
              sx={{ height: 20 }}
            />
            <Chip
              size="small"
              icon={<CaloriesIcon />}
              label={`${meal.recipeStub?.nutrition?.calories || 400}`}
              sx={{ height: 20 }}
            />
          </Box>
        </CardContent>
        {!isSkipped && (
          <Box sx={{ position: 'absolute', top: 4, right: 4, display: 'flex', gap: 0.5 }}>
            <Tooltip title="Swap Recipe">
              <IconButton
                size="small"
                sx={{ bgcolor: 'white', opacity: 0.9, '&:hover': { opacity: 1 } }}
                onClick={(e) => { e.stopPropagation(); setSelectedSlot({ ...meal, slotIndex }); }}
              >
                <SwapIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Skip Meal">
              <IconButton
                size="small"
                sx={{ bgcolor: 'white', opacity: 0.9, '&:hover': { opacity: 1 } }}
                onClick={(e) => { e.stopPropagation(); handleSkipMeal(slotIndex); }}
              >
                <SkipIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}
        {isSkipped && (
          <Chip
            label="Skipped"
            size="small"
            color="warning"
            sx={{ position: 'absolute', top: 8, left: 8 }}
          />
        )}
      </Card>
    );
  };

  const mealsByDay = getMealsByDay();

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Meal Plans
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Plan your week's meals with AI-powered suggestions
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => refetch()}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setGenerateDialogOpen(true)}
            sx={{
              background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
              color: 'white'
            }}
          >
            Generate New Plan
          </Button>
        </Stack>
      </Box>

      {planLoading ? (
        <LinearProgress />
      ) : activePlanData?.getActiveMealPlan ? (
        <>
          {/* Week Overview */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  <CalendarIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    Week of {format(new Date(activePlanData.getActiveMealPlan.weekStart), 'MMMM d, yyyy')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {activePlanData.getActiveMealPlan.planSlots.filter((s: any) => s.status !== 'skipped').length} meals planned
                  </Typography>
                </Box>
              </Box>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={() => {/* TODO: Export functionality */}}
              >
                Export to PDF
              </Button>
            </Box>
          </Paper>

          {/* Meal Grid */}
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'auto repeat(7, 1fr)', gap: 1 }}>
              {/* Header Row */}
              <Box />
              {DAYS.map(day => (
                <Box key={day} sx={{ textAlign: 'center', p: 1 }}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {day}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {mealsByDay[day] && format(new Date(mealsByDay[day].date), 'MMM d')}
                  </Typography>
                </Box>
              ))}

              {/* Meal Rows */}
              {MEAL_TYPES.map(mealType => (
                <React.Fragment key={mealType}>
                  <Box sx={{ p: 1, display: 'flex', alignItems: 'center' }}>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ textTransform: 'capitalize' }}>
                      {mealType}
                    </Typography>
                  </Box>
                  {DAYS.map(day => (
                    <Box key={`${day}-${mealType}`}>
                      <MealCard
                        meal={mealsByDay[day]?.meals[mealType]}
                        slotIndex={mealsByDay[day]?.meals[mealType]?.slotIndex}
                      />
                    </Box>
                  ))}
                </React.Fragment>
              ))}
            </Box>
          </Paper>

          {/* Nutrition Summary */}
          {activePlanData.getActiveMealPlan.nutritionSummary && (
            <Paper sx={{ p: 2, mt: 3 }}>
              <Typography variant="h6" fontWeight="bold" mb={2}>
                Weekly Nutrition Summary
              </Typography>
              <Box display="grid" gridTemplateColumns="repeat(auto-fit, minmax(150px, 1fr))" gap={2}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="caption" color="text.secondary">Avg Daily Calories</Typography>
                    <Typography variant="h5" fontWeight="bold">
                      {Math.round(activePlanData.getActiveMealPlan.nutritionSummary.dailyAverages.calories)}
                    </Typography>
                  </CardContent>
                </Card>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="caption" color="text.secondary">Avg Daily Protein</Typography>
                    <Typography variant="h5" fontWeight="bold">
                      {Math.round(activePlanData.getActiveMealPlan.nutritionSummary.dailyAverages.protein)}g
                    </Typography>
                  </CardContent>
                </Card>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="caption" color="text.secondary">Avg Daily Carbs</Typography>
                    <Typography variant="h5" fontWeight="bold">
                      {Math.round(activePlanData.getActiveMealPlan.nutritionSummary.dailyAverages.carbs)}g
                    </Typography>
                  </CardContent>
                </Card>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="caption" color="text.secondary">Avg Daily Fat</Typography>
                    <Typography variant="h5" fontWeight="bold">
                      {Math.round(activePlanData.getActiveMealPlan.nutritionSummary.dailyAverages.fat)}g
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            </Paper>
          )}
        </>
      ) : (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <RestaurantIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            No Active Meal Plan
          </Typography>
          <Typography variant="body1" color="text.secondary" mb={3}>
            Generate your first meal plan to get started
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<AddIcon />}
            onClick={() => setGenerateDialogOpen(true)}
          >
            Generate Meal Plan
          </Button>
        </Paper>
      )}

      {/* Generate Plan Dialog */}
      <Dialog open={generateDialogOpen} onClose={() => setGenerateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Generate New Meal Plan</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <TextField
              label="Max Prep Time (minutes)"
              type="number"
              value={preferences.maxPrepTime}
              onChange={(e) => setPreferences({ ...preferences, maxPrepTime: parseInt(e.target.value) })}
              fullWidth
            />
            <TextField
              label="Target Daily Calories"
              type="number"
              value={preferences.targetCalories}
              onChange={(e) => setPreferences({ ...preferences, targetCalories: parseInt(e.target.value) })}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Cuisine Preferences</InputLabel>
              <Select
                multiple
                value={preferences.cuisinePreferences}
                onChange={(e) => setPreferences({ ...preferences, cuisinePreferences: e.target.value as string[] })}
              >
                {['Italian', 'Mexican', 'Asian', 'American', 'Mediterranean'].map(cuisine => (
                  <MenuItem key={cuisine} value={cuisine}>{cuisine}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGenerateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleGeneratePlan} variant="contained" disabled={generating}>
            {generating ? 'Generating...' : 'Generate Plan'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Swap Recipe Dialog */}
      {selectedSlot && (
        <Dialog open={!!selectedSlot} onClose={() => setSelectedSlot(null)} maxWidth="md" fullWidth>
          <DialogTitle>Swap Recipe</DialogTitle>
          <DialogContent>
            <Typography variant="body1" mb={2}>
              Select an alternative recipe for {selectedSlot.mealType}
            </Typography>
            {selectedSlot.altOptions?.length > 0 ? (
              <Stack spacing={2}>
                {selectedSlot.altOptions.map((recipe: any) => (
                  <Card key={recipe.id} sx={{ cursor: 'pointer' }} onClick={() => {
                    handleSwapRecipe(selectedSlot.slotIndex, recipe.id);
                    setSelectedSlot(null);
                  }}>
                    <CardContent>
                      <Box display="flex" justifyContent="space-between">
                        <Box>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {recipe.title}
                          </Typography>
                          <Box display="flex" gap={2} mt={1}>
                            <Chip size="small" icon={<TimerIcon />} label={`${recipe.readyInMinutes}m`} />
                            <Chip size="small" icon={<CaloriesIcon />} label={`${recipe.nutrition?.calories || 0} cal`} />
                          </Box>
                        </Box>
                        {recipe.image && (
                          <Avatar src={recipe.image} variant="rounded" sx={{ width: 60, height: 60 }} />
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            ) : (
              <Alert severity="info">No alternative recipes available</Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSelectedSlot(null)}>Close</Button>
          </DialogActions>
        </Dialog>
      )}
      <RecipeModal open={!!selectedRecipe} onClose={() => setSelectedRecipe(null)} recipe={selectedRecipe} />
    </Box>
  );
};

export default MealPlans;
